import { ChevronRight, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import Link from "@/components/ui/link";
import { cn } from "@/lib/utils";

export type DetailInfoTone = "neutral" | "attention" | "live" | "muted";

/**
 * The liquid-glass well every detail-page row leads with.
 *
 * Low-alpha tint + backdrop blur AND saturate (the saturate is what sells the
 * glass), an inset top highlight for the lit edge, and a soft drop shadow so
 * the well floats off the page. Tones tint the same glass rather than swap it
 * for a flat fill, so an "attention" row still reads as one of the set.
 */
const TILE_TONE_CLASS: Record<DetailInfoTone, string> = {
  neutral:
    "border-white/70 bg-white/60 text-text-secondary dark:border-white/[0.14] dark:bg-white/[0.07] dark:text-white/80",
  attention:
    "border-warning-border bg-warning-bg/70 text-warning dark:border-[#F5E6B8]/25 dark:bg-[#F5E6B8]/[0.10] dark:text-[#F5E6B8]",
  live: "border-success-border bg-success-bg/70 text-success dark:border-emerald-300/25 dark:bg-emerald-300/[0.10] dark:text-emerald-200",
  muted:
    "border-white/50 bg-white/40 text-text-tertiary dark:border-white/[0.08] dark:bg-white/[0.04] dark:text-white/45",
};

const TILE_SIZE_CLASS = {
  md: "size-11 rounded-[14px] [&_svg]:size-5",
  sm: "size-9 rounded-xl [&_svg]:size-[18px]",
} as const;

export function DetailInfoIcon({
  icon: Icon,
  children,
  tone = "neutral",
  size = "md",
  className,
}: {
  /** The icon to draw. Pass `children` instead for anything else (a live dot). */
  icon?: LucideIcon;
  children?: ReactNode;
  tone?: DetailInfoTone;
  size?: keyof typeof TILE_SIZE_CLASS;
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "relative flex shrink-0 items-center justify-center border",
        "shadow-[inset_0_1px_0_rgba(255,255,255,0.75),0_4px_14px_-6px_rgba(15,10,40,0.22)] backdrop-blur-xl backdrop-saturate-150",
        "dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.14),0_6px_18px_-8px_rgba(0,0,0,0.7)]",
        TILE_SIZE_CLASS[size],
        TILE_TONE_CLASS[tone],
        className,
      )}
    >
      {Icon ? <Icon strokeWidth={1.9} /> : children}
    </span>
  );
}

type RowAction =
  | { href: string; onClick?: never }
  | { onClick: () => void; href?: never }
  | { href?: undefined; onClick?: undefined };

type DetailInfoRowProps = RowAction & {
  icon?: LucideIcon;
  /** Replaces the icon inside the well (a live dot, a logo). */
  iconContent?: ReactNode;
  tone?: DetailInfoTone;
  size?: "md" | "sm";
  title: ReactNode;
  subtitle?: ReactNode;
  /** A third, quieter line — a caveat under the subtitle. */
  note?: ReactNode;
  /** Colour override for the title (a tone's own title colour). */
  titleClassName?: string;
  /** Let the subtitle wrap instead of truncating. */
  wrapSubtitle?: boolean;
  /** Forced off for an interactive row that should not show one. */
  chevron?: boolean;
  ariaLabel?: string;
  className?: string;
};

/**
 * The one icon + title + subtitle (+ chevron) row for the hiive, event, venue
 * and session pages: location, reviews, contact, dates, policies.
 *
 * These used to be written inline per page, and had drifted into four tile
 * styles, three chevron colours and a hiive page with a padded `rounded-lg`
 * well of its own. One component keeps them one set. It renders a `Link`
 * with `href`, a button with `onClick`, and a plain row with neither — and
 * only an interactive row gets the chevron.
 */
export function DetailInfoRow({
  icon,
  iconContent,
  tone = "neutral",
  size = "md",
  title,
  subtitle,
  note,
  titleClassName,
  wrapSubtitle = false,
  chevron,
  ariaLabel,
  className,
  href,
  onClick,
}: DetailInfoRowProps) {
  const interactive = Boolean(href || onClick);
  const showChevron = chevron ?? interactive;
  const rowClassName = cn(
    "flex w-full items-center gap-3 rounded-2xl text-left",
    interactive &&
      "transition-transform duration-150 ease-[var(--ease-out-strong)] active:scale-[0.99]",
    className,
  );

  const body = (
    <>
      <DetailInfoIcon icon={icon} tone={tone} size={size}>
        {iconContent}
      </DetailInfoIcon>
      <span className="flex min-w-0 flex-1 flex-col">
        <span
          className={cn(
            "truncate text-sm font-medium text-text-primary dark:text-white",
            titleClassName,
          )}
        >
          {title}
        </span>
        {subtitle ? (
          <span
            className={cn(
              "mt-0.5 text-xs text-text-secondary dark:text-white/55",
              wrapSubtitle ? "leading-snug" : "truncate",
            )}
          >
            {subtitle}
          </span>
        ) : null}
        {note ? (
          <span className="mt-0.5 truncate text-xs text-text-tertiary dark:text-white/40">
            {note}
          </span>
        ) : null}
      </span>
      {showChevron ? (
        <ChevronRight
          aria-hidden="true"
          className="size-5 shrink-0 text-text-tertiary dark:text-white/40"
        />
      ) : null}
    </>
  );

  if (href) {
    return (
      <Link href={href} aria-label={ariaLabel} className={rowClassName}>
        {body}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        className={rowClassName}
      >
        {body}
      </button>
    );
  }
  return <div className={rowClassName}>{body}</div>;
}

/**
 * A bordered list of `DetailInfoRow`s — Terms, FAQs, House rules — the one
 * grouped treatment for every detail page. Rows inside take
 * `DETAIL_INFO_GROUP_ROW` so they pad to the card and share its dividers.
 */
export function DetailInfoGroup({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "divide-y divide-border overflow-hidden rounded-2xl border border-border bg-surface shadow-sm",
        "dark:divide-white/[0.08] dark:border-white/[0.1] dark:bg-white/[0.02] dark:shadow-none",
        className,
      )}
    >
      {children}
    </div>
  );
}

export const DETAIL_INFO_GROUP_ROW =
  "rounded-none px-4 py-3 active:scale-100 hover:bg-surface-alt dark:hover:bg-white/[0.04]";
